// Animated pictures in the frames (GIF / animated WebP; contracts section 8).
//
// Decode: once per hung picture (anim-decode.ts, frame by frame). Each frame is cover-cropped to the slot's picture
// aspect and scaled (at most CAP_PX on the long side for the slot kind, never up), and kept as RGBA bytes of that
// size; the texture is a DataTexture whose data is swapped per frame (texSubImage2D from memory: no canvas in between). A tile (tile.ts: a small square image) keeps its own size and is not cropped. The first
// frame shows as soon as it is decoded; the animation starts when every frame is in.
//
// Memory: the frames of all animations in the room share BUDGET bytes (w x h x 4 per frame). An animation that does
// not fit is scaled down to fit (to at most half its size on each side); beyond that it shows its first frame only.
// Its share goes back when the picture is taken down.
//
// Playback: one timer for all animations. A tick advances each animation whose picture was drawn in the latest
// render (the picture mesh's onBeforeRender runs only when three's frustum test keeps it, so a picture out of view
// is not advanced), at the image's own frame timing (time-based: frames are skipped rather than slowed), at most
// MAX_FPS ticks per second. A changed frame becomes the texture's data and invalidate() asks for one render.
// No visible animation, or a hidden page: the timer stops; the next render that draws an animated picture starts it.

import { DataTexture, LinearFilter, LinearMipmapLinearFilter, RGBAFormat, UnsignedByteType, type Mesh, type WebGLRenderer } from 'three';
import { decodeFrames } from './anim-decode';
import { isTile } from './tile';
import { ART_ANIM_MAX_FRAMES, type ArtEntry } from '@lr3d/shared';
import { cropRect, type Slot } from './slots';

/** Long side of an animation's canvas per slot kind (px), desktop and mobile tier. */
const CAP_PX: Record<string, { desktop: number; mobile: number }> = { panel: { desktop: 800, mobile: 400 }, xl: { desktop: 1200, mobile: 800 } };
const CAP_DEFAULT = { desktop: 800, mobile: 400 };
/** Decoded frames of all animations in the room, bytes. */
const BUDGET = { desktop: 64 * 1024 * 1024, mobile: 24 * 1024 * 1024 };
const MAX_FPS = { desktop: 30, mobile: 15 };
/** An animation over the budget may shrink to this fraction of its size per side before it stays still. */
const MIN_SCALE = 0.5;

interface Anim {
  /** cumulative end time of each frame, ms */
  ends: number[];
  total: number;
  /** all frames decoded */
  done: boolean;
  /** RGBA rows top first (the texture is not flipped on upload: the caller mirrors v) */
  frameData: Uint8Array[];
  texture: DataTexture | null;
  shown: number;
  /** renderer.info.render.frame of the last render that drew this picture */
  seen: number;
  /** a frame was drawn into the canvas and not yet uploaded */
  pending: boolean;
  bytes: number;
  start: number;
  abort: { aborted: boolean };
  /** kept at its first frame (over the budget) */
  still: boolean;
}

export interface AnimLoad {
  /** rows top first: mirror v in the texture transform */
  texture: DataTexture;
  /** hook for the picture mesh (marks it seen in this render) */
  attach(mesh: Mesh): void;
  /** stop decoding, close the frames, free the budget share (the texture is the caller's to dispose) */
  release(): void;
}

export class ArtAnimator {
  readonly stats = { animated: 0, visible: 0, ticks: 0, draws: 0, uploads: 0, tickMs: 0, decodeMs: 0, bytes: 0, stills: 0, scaled: 0 };
  private anims = new Set<Anim>();
  private timer = 0;
  private used = 0;
  private tier: 'desktop' | 'mobile' = 'desktop';
  private invalidate: () => void = () => {};
  /** dispose() ran: loads still fetching are refused (they would take budget and keep frames nobody frees). */
  private disposed = false;
  private onVisibility = () => { if (!document.hidden) this.arm(0); };

  constructor(private renderer: WebGLRenderer) {
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  configure(o: { mobile: boolean; invalidate: () => void }): void {
    this.tier = o.mobile ? 'mobile' : 'desktop';
    this.invalidate = o.invalidate;
  }

  get budget(): number { return BUDGET[this.tier]; }

  /**
   * Fetch and decode an animated picture for `slot`. Resolves with the texture once the first frame is in; the rest
   * decode afterwards. Rejects when the file cannot be decoded.
   */
  async load(slot: Slot, entry: ArtEntry, url: string): Promise<AnimLoad> {
    const abort = { aborted: false };
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const buf = new Uint8Array(await res.arrayBuffer());
    if (this.disposed) throw new Error('animator disposed');
    const t0 = performance.now();
    const tile = isTile(entry.w, entry.h);
    const n = Math.min(ART_ANIM_MAX_FRAMES, entry.frames ?? 1);
    // the source rectangle (cover crop at the picture aspect of the frame it hangs in, with and without a mat: slots.ts
    // cropRect; a tile: the whole image) and the canvas size
    const { sw, sh } = tile ? { sw: entry.w, sh: entry.h } : cropRect(slot, entry.w, entry.h);
    const cap = (CAP_PX[slot.kind] ?? CAP_DEFAULT)[this.tier];
    let k = tile ? 1 : Math.min(1, cap / Math.max(sw, sh));
    let w = Math.max(1, Math.round(sw * k)), h = Math.max(1, Math.round(sh * k));
    const free = this.budget - this.used;
    let still = false;
    if (w * h * 4 * n > free) {
      const s = Math.sqrt(Math.max(0, free) / (w * h * 4 * n));
      if (s >= MIN_SCALE && !tile) {
        k *= s;
        w = Math.max(1, Math.floor(sw * k)); h = Math.max(1, Math.floor(sh * k));
        this.stats.scaled++;
      } else {
        still = true;
        this.stats.stills++;
      }
    }
    const bytes = w * h * 4 * (still ? 1 : n);
    this.used += bytes;
    const a: Anim = { frameData: [], ends: [], total: 0, done: false, texture: null, shown: 0, seen: -1, pending: false, bytes, start: performance.now(), abort, still };
    this.anims.add(a);
    this.updateStats();
    const scratch = document.createElement('canvas');
    scratch.width = w; scratch.height = h;
    const sg = scratch.getContext('2d', { willReadFrequently: true })!;
    sg.imageSmoothingEnabled = !tile;
    sg.imageSmoothingQuality = 'high';
    const sx = (entry.w - sw) / 2, sy = (entry.h - sh) / 2;
    let first: (() => void) | null = null;
    let fail: ((e: unknown) => void) | null = null;
    const firstIn = new Promise<void>((res, rej) => { first = res; fail = rej; });
    let t = 0;
    const run = decodeFrames(buf, async (src, i, delay) => {
      if (abort.aborted) return false;
      sg.fillStyle = '#fff'; // transparent parts on white (the mat), as for still uploads
      sg.fillRect(0, 0, w, h);
      sg.drawImage(src, sx, sy, sw, sh, 0, 0, w, h);
      const data = new Uint8Array(sg.getImageData(0, 0, w, h).data.buffer);
      a.frameData.push(data);
      a.ends.push((t += delay));
      if (i === 0) {
        const tex = new DataTexture(data, w, h, RGBAFormat, UnsignedByteType);
        tex.magFilter = LinearFilter;
        tex.minFilter = LinearMipmapLinearFilter;
        tex.generateMipmaps = true;
        tex.needsUpdate = true;
        a.texture = tex;
        first?.();
      }
      return !still;
    }, n, abort).then((r) => {
      scratch.width = scratch.height = 0;
      if (abort.aborted) return;
      if (!r || !a.frameData.length) throw new Error('no frames');
      a.total = t;
      a.done = true;
      a.start = performance.now();
      this.stats.decodeMs += performance.now() - t0;
      this.updateStats();
      this.arm(0);
    });
    run.catch((e) => { scratch.width = scratch.height = 0; fail?.(e); });
    try {
      await firstIn;
    } catch (e) {
      this.release(a);
      throw e;
    }
    return {
      release: () => this.release(a),
      texture: a.texture!,
      attach: (mesh) => {
        mesh.onBeforeRender = () => {
          a.seen = this.renderer.info.render.frame;
          if (a.pending) { a.pending = false; this.stats.uploads++; }
          if (!this.timer && a.done && a.frameData.length > 1) this.arm(1000 / MAX_FPS[this.tier]);
        };
      },
    };
  }

  /** An animation goes: decoding stops, frames are closed, its budget share is free again. */
  private release(a: Anim): void {
    if (!this.anims.has(a)) return;
    a.abort.aborted = true;
    a.frameData.length = 0;
    this.used -= a.bytes;
    this.anims.delete(a);
    this.updateStats();
  }

  private arm(ms: number): void {
    if (this.timer || document.hidden) return;
    this.timer = window.setTimeout(() => this.tick(), ms);
  }

  private tick(): void {
    this.timer = 0;
    if (document.hidden) return;
    const t0 = performance.now();
    const frame = this.renderer.info.render.frame;
    let changed = false;
    let visible = 0;
    let next = Infinity;
    for (const a of this.anims) {
      if (!a.done || a.frameData.length < 2 || a.seen !== frame || !a.texture) continue;
      visible++;
      const t = (t0 - a.start) % a.total;
      let i = 0;
      while (i < a.ends.length - 1 && a.ends[i]! <= t) i++;
      next = Math.min(next, a.ends[i]! - t);
      if (i !== a.shown) {
        a.shown = i;
        (a.texture.image as { data: Uint8Array }).data = a.frameData[i]!;
        a.texture.needsUpdate = true;
        a.pending = true;
        changed = true;
        this.stats.draws++;
      }
    }
    this.stats.visible = visible;
    this.stats.ticks++;
    if (changed) this.invalidate();
    this.stats.tickMs += performance.now() - t0;
    if (visible) this.arm(Math.max(1000 / MAX_FPS[this.tier], next));
  }

  private updateStats(): void {
    this.stats.animated = [...this.anims].filter((a) => !a.still).length;
    this.stats.bytes = this.used;
  }

  dispose(): void {
    this.disposed = true;
    for (const a of [...this.anims]) this.release(a);
    clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }
}
